import React from 'react';
import { EnquiryFormField } from '../types';

/**
 * The consent label, with its policy tokens turned into links.
 *
 * The backoffice writes `[privacy_policy]` and `[terms_and_conditions]` into the label text and stores the
 * URLs separately, so the wording and the destinations can change without touching this code. A token whose
 * URL is missing is rendered as plain text rather than a dead link - the offers API refuses to save that
 * combination, so it only happens to a form saved before that rule existed.
 */

const PRIVACY_TOKEN = '[privacy_policy]';
const TERMS_TOKEN = '[terms_and_conditions]';

const PRIVACY_TEXT = 'Privacy Collection Statement';
const TERMS_TEXT = 'Terms and Conditions';

interface ConsentLabelProps {
  field: EnquiryFormField;
}

export const ConsentLabel: React.FC<ConsentLabelProps> = ({ field }) => {
  const segments = splitOnTokens(field.fieldLabel ?? '');

  return (
    <span data-testid="enquiry-consent-label">
      {segments.map((segment, index) => {
        // Case-insensitively, because the offers API accepts a token in any casing - it checks for one with
        // OrdinalIgnoreCase - so "[Privacy_Policy]" is stored as valid and must not render as dead text.
        if (isToken(segment, PRIVACY_TOKEN)) {
          return (
            <PolicyLink
              key={index}
              url={field.privacyPolicyURL}
              text={PRIVACY_TEXT}
              usePopup={field.usePopup}
            />
          );
        }

        if (isToken(segment, TERMS_TOKEN)) {
          return (
            <PolicyLink
              key={index}
              url={field.termsConditionURL}
              text={TERMS_TEXT}
              usePopup={field.usePopup}
            />
          );
        }

        return <React.Fragment key={index}>{segment}</React.Fragment>;
      })}
    </span>
  );
};

const PolicyLink: React.FC<{
  url: string | null;
  text: string;
  usePopup: boolean;
}> = ({ url, text, usePopup }) => {
  if (!url) {
    return <strong>{text}</strong>;
  }

  return (
    <a
      href={url}
      target={usePopup ? undefined : '_blank'}
      rel="noopener noreferrer"
      // The checkbox wraps this label, so a click on the link would otherwise tick the box as well.
      onClick={(event) => event.stopPropagation()}
    >
      {text}
    </a>
  );
};

/** Whether a segment is the given token, in whatever casing it was authored. */
export function isToken(segment: string, token: string): boolean {
  return segment.toLowerCase() === token.toLowerCase();
}

/** Splits a label into text and token parts, keeping the tokens so they can be replaced in place. */
export function splitOnTokens(label: string): string[] {
  return label
    .split(
      new RegExp(
        `(${escapeRegExp(PRIVACY_TOKEN)}|${escapeRegExp(TERMS_TOKEN)})`,
        'gi'
      )
    )
    .filter((segment) => segment.length > 0);
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
